import * as React from "react";
import { ComboBox, Item, Flex, View, Text } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import type { Key } from "@react-types/shared";
import { IFilterControl, IFilterItem } from "../../FilterSetupPlaceholder";
import { useDependency } from "../../../../../../../contexts/DependencyProvider";
import { ListUserRequest } from "../../../../../../../services/soap/access/requests/ListUserRequest";
import { GetUserRequest } from "../../../../../../../services/soap/access/requests/GetUserRequest";
import { useFilterGuard } from "../../../../../../../hooks/useFilterGuard";
import styles from "./UserFilterControl.module.css";
import { OperatorSelector, IOperator } from "../OperatorSelector";

/**
 * Props for the UserFilterControl component
 */
export interface IUserFilterControlProps {
    /** Filter control configuration object */
    filter: IFilterControl;
    /** Callback function called when filter values change */
    onFilterChange: (filter: IFilterControl) => void;
}

interface User {
    id: number;
    name: string;
}

const USER_OPERATORS: IOperator[] = [
    { key: "current_user", label: "Current User" },
    { key: "selected_user", label: "Selected User" },
];

export const UserFilterControl = React.forwardRef<
    HTMLDivElement,
    IUserFilterControlProps
>((props, forwardedRef) => {
    const { filter, onFilterChange } = props;
    const { notifyChange, syncGuard } = useFilterGuard(onFilterChange);
    const { accessService, store } = useDependency();
    const apiDebounceRef = React.useRef<NodeJS.Timeout | null>(null);
    const isRestoringFilterRef = React.useRef<boolean>(false);
    const lastRestoredValueRef = React.useRef<string | null>(null);
    const lastRestoredOperatorRef = React.useRef<string | null>(null);
    const lastProcessedFilterRef = React.useRef<string | null>(null);
    const lastCommittedFilterRef = React.useRef<{
        value: string;
        valueLabel: string;
        operator: string;
        isCurrentUser: boolean;
    } | null>(null);

    const currentFilterItem = React.useMemo(
        () =>
            filter.filterInfo[0] || {
                name: "value",
                type: "in",
                value: "",
            },
        [filter.filterInfo]
    );

    React.useEffect(() => {
        syncGuard(filter);
    }, [filter, syncGuard]);

    // Initialize operator from currentFilterItem - check isCurrentUser for backward compatibility
    const getInitialOperator = () => {
        if (currentFilterItem.isCurrentUser) {
            return "current_user";
        }
        // Check if type is already set to one of our operators
        if (
            currentFilterItem.type === "current_user" ||
            currentFilterItem.type === "selected_user"
        ) {
            return currentFilterItem.type;
        }
        return "selected_user";
    };

    // Helper function to check if filter values have changed compared to last committed state
    const hasFilterValueChanged = React.useCallback(
        (newValue: string, newValueLabel: string, newOperator: string, newIsCurrentUser: boolean): boolean => {
            const lastCommitted = lastCommittedFilterRef.current;
            
            // If no committed state exists, consider it changed
            if (!lastCommitted) {
                return true;
            }

            // Compare all relevant properties
            return (
                lastCommitted.value !== newValue ||
                lastCommitted.valueLabel !== newValueLabel ||
                lastCommitted.operator !== newOperator ||
                lastCommitted.isCurrentUser !== newIsCurrentUser
            );
        },
        []
    );

    const [selectedKey, setSelectedKey] = React.useState<Key | null>(
        currentFilterItem.value ? String(currentFilterItem.value) : null
    );
    const [inputValue, setInputValue] = React.useState<string>(
        currentFilterItem.valueLabel || ""
    );
    const [operator, setOperator] = React.useState<string>(
        getInitialOperator()
    );
    const [currentUserInfo, setCurrentUserInfo] = React.useState<{
        id: number;
        name: string;
    } | null>(null);

    // When this filter represents "Current User", display the session user at runtime
    // without mutating the parent filter state (prevents unintended dirty state).
    React.useEffect(() => {
        const isCurrentUser =
            currentFilterItem?.isCurrentUser === true ||
            operator === "current_user";

        if (!isCurrentUser || !currentUserInfo) {
            return;
        }

        const nextKey = String(currentUserInfo.id);
        const nextLabel = currentUserInfo.name;

        setSelectedKey((prev) => {
            const prevStr = prev ? String(prev) : null;
            return prevStr === nextKey ? prev : nextKey;
        });
        setInputValue((prev) => (prev === nextLabel ? prev : nextLabel));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUserInfo, currentFilterItem?.isCurrentUser, operator]);

    // Initialize lastCommittedFilterRef on mount
    React.useEffect(() => {
        if (currentFilterItem && !lastCommittedFilterRef.current) {
            const initialOperator = currentFilterItem.isCurrentUser === true
                ? "current_user"
                : "selected_user";
            
            lastCommittedFilterRef.current = {
                value: currentFilterItem.value || "",
                valueLabel: currentFilterItem.valueLabel || "",
                operator: initialOperator,
                isCurrentUser: currentFilterItem.isCurrentUser || false,
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount - initialization only

    // Fetch current user data on mount
    React.useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const response = await accessService.getUser(
                    new GetUserRequest(
                        store.Server,
                        store.SessionId,
                        store.UserId.toString()
                    )
                );
                if (response?.USER) {
                    const userName =
                        response.USER.contactName ||
                        response.USER.name1 ||
                        response.USER.userName ||
                        String(response.USER.id);
                    setCurrentUserInfo({
                        id: response.USER.id,
                        name: userName,
                    });
                }
            } catch (error) {
                console.error("Error fetching current user:", error);
            }
        };

        fetchCurrentUser();
    }, [accessService, store.Server, store.SessionId, store.UserId]);

    // Sync with filter changes from parent
    React.useEffect(() => {
        const currentItem = filter.filterInfo[0];

        // Create a signature of the current filter state to detect if it actually changed
        const filterSignature = currentItem
            ? `${currentItem.value || ""}:${currentItem.valueLabel || ""}:${
                  currentItem.type || ""
              }:${currentItem.isCurrentUser || false}`
            : "empty";

        // Skip if filter hasn't actually changed (same signature)
        if (lastProcessedFilterRef.current === filterSignature) {
            return;
        }

        // Skip if we're in the middle of restoring filter (prevent infinite loop)
        if (isRestoringFilterRef.current) {
            isRestoringFilterRef.current = false;
            lastProcessedFilterRef.current = filterSignature;
            return;
        }

        // Mark this filter state as processed
        lastProcessedFilterRef.current = filterSignature;
        if (currentItem) {
            // Convert 'contains' to 'selected_user' for backward compatibility
            if (currentItem.type === "contains") {
                const updatedFilterInfo: IFilterItem[] = [
                    {
                        ...currentItem,
                        type: "in",
                        isCurrentUser: false,
                    },
                ];
                notifyChange({
                    ...filter,
                    filterInfo: updatedFilterInfo,
                });
                setOperator("selected_user");
                return;
            }
            // If this filter is "Current User", display the runtime session user name/id
            // rather than any persisted valueLabel from the Custom View definition.
            const shouldUseRuntimeCurrentUser =
                currentItem.isCurrentUser === true && !!currentUserInfo;

            const newSelectedKey = shouldUseRuntimeCurrentUser
                ? String(currentUserInfo!.id)
                : currentItem.value
                ? String(currentItem.value)
                : null;

            const newInputValue = shouldUseRuntimeCurrentUser
                ? currentUserInfo!.name
                : currentItem.valueLabel || "";

            // Protect against parent clearing valid selection: If we have a valid selection and parent sends empty,
            // preserve the selection (parent transformation may have lost the data temporarily)
            // Only update if parent actually has a value, or if we don't have a selection yet
            const hasValidSelection = selectedKey && inputValue;
            const parentHasEmptyValue = !newSelectedKey && !newInputValue;
            const shouldPreserveSelection =
                hasValidSelection &&
                parentHasEmptyValue &&
                operator === "selected_user";

            if (shouldPreserveSelection) {
                // Check if filter already has the correct values to prevent infinite loop
                const filterAlreadyCorrect =
                    currentItem.value === String(selectedKey) &&
                    currentItem.valueLabel === inputValue &&
                    currentItem.type === "in";

                // Check if we've already tried to restore this exact value (prevents infinite loop)
                const restoreKey = `${selectedKey}:${inputValue}`;
                const alreadyRestoredThisValue =
                    lastRestoredValueRef.current === restoreKey;

                if (!filterAlreadyCorrect && !alreadyRestoredThisValue) {
                    // Don't clear the selection, but restore the filterInfo with correct values
                    const updatedFilterInfo: IFilterItem[] = [
                        {
                            ...currentItem,
                            type: "in",
                            value: String(selectedKey),
                            valueLabel: inputValue,
                            isCurrentUser: false,
                        },
                    ];
                    const didNotify = notifyChange({
                        ...filter,
                        filterInfo: updatedFilterInfo,
                    });
                    if (didNotify) {
                        isRestoringFilterRef.current = true;
                        lastRestoredValueRef.current = restoreKey;
                    }
                }
            } else {
                // Reset restore tracking when we're not preserving (new selection or cleared)
                if (!newSelectedKey || !newInputValue) {
                    lastRestoredValueRef.current = null;
                }
                // Reset operator restore tracking when operator changes
                if (operator !== "current_user") {
                    lastRestoredOperatorRef.current = null;
                }
                setSelectedKey(newSelectedKey);
                setInputValue(newInputValue);
            }

            // Update operator based on isCurrentUser flag (backward compatibility)
            // IMPORTANT: If operator is already "current_user" and isCurrentUser is not explicitly false,
            // preserve the current_user operator (parent may have lost the flag during transformation)
            const hasIsCurrentUserFlag = "isCurrentUser" in currentItem;

            if (currentItem.isCurrentUser === true) {
                setOperator("current_user");
            } else if (currentItem.isCurrentUser === false) {
                // Explicitly set to false, so use selected_user
                setOperator("selected_user");
            } else if (
                currentItem.type === "current_user" ||
                currentItem.type === "selected_user"
            ) {
                setOperator(currentItem.type);
            } else if (operator === "current_user" && !hasIsCurrentUserFlag) {
                // Preserve current_user operator if filter doesn't explicitly say otherwise
                // This handles the case where parent transforms filter and loses isCurrentUser flag
                // Don't change operator, but restore the flag in filterInfo
                // Check if we've already restored this operator to prevent infinite loop
                const restoreKey = "current_user_operator";
                const alreadyRestoredOperator =
                    lastRestoredOperatorRef.current === restoreKey;

                if (!alreadyRestoredOperator) {
                    const updatedFilterInfo: IFilterItem[] = [
                        {
                            ...currentItem,
                            isCurrentUser: true,
                        },
                    ];
                    const didNotify = notifyChange({
                        ...filter,
                        filterInfo: updatedFilterInfo,
                    });
                    if (didNotify) {
                        isRestoringFilterRef.current = true;
                        lastRestoredOperatorRef.current = restoreKey;
                    }
                }
            } else {
                setOperator("selected_user");
            }

            // Sync lastCommittedFilterRef when filter changes come from parent
            // This ensures we track the actual committed state from parent updates
            if (currentItem) {
                const currentOperator = currentItem.isCurrentUser === true
                    ? "current_user"
                    : currentItem.isCurrentUser === false || currentItem.type === "selected_user" || currentItem.type === "in"
                    ? "selected_user"
                    : operator;
                
                lastCommittedFilterRef.current = {
                    value: currentItem.value || "",
                    valueLabel: currentItem.valueLabel || "",
                    operator: currentOperator,
                    isCurrentUser: currentItem.isCurrentUser || false,
                };
            }
        } else {
            // Empty filter state - sync lastCommittedFilterRef
            lastCommittedFilterRef.current = {
                value: "",
                valueLabel: "",
                operator: "selected_user",
                isCurrentUser: false,
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.filterInfo]);

    const list = useAsyncList<User>({
        async load({ cursor, filterText }) {
            // Don't search if filterText is empty or too short
            if (!filterText || filterText.trim().length < 2) {
                return { items: [], cursor: undefined };
            }

            // Clear any existing debounce timeout
            if (apiDebounceRef.current) {
                clearTimeout(apiDebounceRef.current);
            }

            // Debounce API calls by 500ms
            return new Promise((resolve) => {
                apiDebounceRef.current = setTimeout(async () => {
                    try {
                        const offset = cursor ? Number(cursor) : 0;
                        const limit = 50;

                        const response = await accessService.listUser(
                            new ListUserRequest(
                                store.Server,
                                store.SessionId,
                                true, // onlyActive
                                limit,
                                offset,
                                undefined, // orderBy
                                false, // countOnly
                                undefined, // accessFilter
                                undefined, // groupId
                                undefined, // notPublic
                                false, // public
                                undefined, // roleId
                                undefined, // treeHandle
                                filterText // userName - for search
                            )
                        );
                        // Extract users from response
                        const users = Array.isArray(response?.USERS)
                            ? response?.USERS?.map((u: any) => u.USER)
                            : [(response?.USERS as any)?.USER];
                        // Map to required format
                        const mappedUsers: User[] = users.map((user: any) => ({
                            id: user.id,
                            name:
                                user.contactName ||
                                user.name1 ||
                                user.userName ||
                                String(user.id),
                        }));

                        resolve({
                            items: mappedUsers,
                            cursor:
                                mappedUsers.length === limit
                                    ? String(offset + limit)
                                    : undefined,
                        });
                    } catch (error) {
                        console.error("Error fetching users:", error);
                        resolve({ items: [], cursor: undefined });
                    }
                }, 500);
            });
        },
    });

    const handleSelectionChange = React.useCallback(
        (key: Key | null) => {
            // Don't allow selection changes when current user operator is selected
            if (operator === "current_user") {
                return;
            }

            // Check if the key actually changed
            const currentKeyString = selectedKey ? String(selectedKey) : null;
            const newKeyString = key ? String(key) : null;
            
            if (currentKeyString === newKeyString) {
                // Selection hasn't changed, no need to update
                return;
            }

            setSelectedKey(key);

            if (key === null) {
                // Clear the filter
                const newValue = "";
                const newValueLabel = "";
                const newOperator = "selected_user";
                const newIsCurrentUser = false;

                // Only update if values actually changed
                if (hasFilterValueChanged(newValue, newValueLabel, newOperator, newIsCurrentUser)) {
                    const updatedFilterInfo: IFilterItem[] = [
                        {
                            ...currentFilterItem,
                            type: "in",
                            value: newValue,
                            valueLabel: newValueLabel,
                            isCurrentUser: newIsCurrentUser,
                        },
                    ];
                    const didNotify = notifyChange({
                        ...filter,
                        filterInfo: updatedFilterInfo,
                    });
                    if (didNotify) {
                        // Update last committed filter ref
                        lastCommittedFilterRef.current = {
                            value: newValue,
                            valueLabel: newValueLabel,
                            operator: newOperator,
                            isCurrentUser: newIsCurrentUser,
                        };
                    }
                }
                setInputValue("");
            } else {
                // Find selected user in the list - it should always be there when selected from dropdown
                const selectedUser = list.items.find(
                    (u) => String(u.id) === String(key)
                );
                if (selectedUser) {
                    const newValue = String(selectedUser.id);
                    const newValueLabel = selectedUser.name;
                    const newOperator = "selected_user";
                    const newIsCurrentUser = false;

                    // Only update if values actually changed
                    if (hasFilterValueChanged(newValue, newValueLabel, newOperator, newIsCurrentUser)) {
                        const updatedFilterInfo: IFilterItem[] = [
                            {
                                ...currentFilterItem,
                                type: "in",
                                value: newValue,
                                valueLabel: newValueLabel,
                                isCurrentUser: newIsCurrentUser,
                            },
                        ];

                        const didNotify = notifyChange({
                            ...filter,
                            filterInfo: updatedFilterInfo,
                        });
                        if (didNotify) {
                            // Update last committed filter ref
                            lastCommittedFilterRef.current = {
                                value: newValue,
                                valueLabel: newValueLabel,
                                operator: newOperator,
                                isCurrentUser: newIsCurrentUser,
                            };
                        }
                    }

                    setInputValue(selectedUser.name);
                    // Reset restore tracking when user makes a new selection
                    lastRestoredValueRef.current = null;
                } else {
                    // User not found in current list - this shouldn't happen normally, but handle it gracefully
                    // Preserve existing valueLabel if we have one, otherwise use empty string
                    console.warn(
                        `User with id ${key} not found in current list`
                    );
                    const newValue = String(key);
                    const newValueLabel = currentFilterItem.name || "";
                    const newOperator = "selected_user";
                    const newIsCurrentUser = false;

                    // Only update if values actually changed
                    if (hasFilterValueChanged(newValue, newValueLabel, newOperator, newIsCurrentUser)) {
                        const updatedFilterInfo: IFilterItem[] = [
                            {
                                ...currentFilterItem,
                                type: "in",
                                value: newValue,
                                valueLabel: newValueLabel,
                                isCurrentUser: newIsCurrentUser,
                            },
                        ];

                        const didNotify = notifyChange({
                            ...filter,
                            filterInfo: updatedFilterInfo,
                        });
                        if (didNotify) {
                            // Update last committed filter ref
                            lastCommittedFilterRef.current = {
                                value: newValue,
                                valueLabel: newValueLabel,
                                operator: newOperator,
                                isCurrentUser: newIsCurrentUser,
                            };
                        }
                    }
                }
            }
        },
        [filter, currentFilterItem, notifyChange, list.items, operator, selectedKey, hasFilterValueChanged]
    );

    const handleInputChange = React.useCallback(
        (value: string) => {
            // Don't allow input changes when current user operator is selected
            if (operator === "current_user") {
                return;
            }
            setInputValue(value);
            // Clear selection if input is cleared
            if (!value) {
                // Check if current state is already empty
                const currentValue = currentFilterItem.value || "";
                const currentValueLabel = currentFilterItem.valueLabel || "";
                const isAlreadyEmpty = !currentValue && !currentValueLabel;

                if (!isAlreadyEmpty) {
                    setSelectedKey(null);
                    const newValue = "";
                    const newValueLabel = "";
                    const newOperator = "selected_user";
                    const newIsCurrentUser = false;

                    // Only update if values actually changed
                    if (hasFilterValueChanged(newValue, newValueLabel, newOperator, newIsCurrentUser)) {
                        const updatedFilterInfo: IFilterItem[] = [
                            {
                                ...currentFilterItem,
                                type: "in",
                                value: newValue,
                                valueLabel: newValueLabel,
                                isCurrentUser: newIsCurrentUser,
                            },
                        ];
                        const didNotify = notifyChange({
                            ...filter,
                            filterInfo: updatedFilterInfo,
                        });
                        if (didNotify) {
                            // Update last committed filter ref
                            lastCommittedFilterRef.current = {
                                value: newValue,
                                valueLabel: newValueLabel,
                                operator: newOperator,
                                isCurrentUser: newIsCurrentUser,
                            };
                        }
                    }
                }
            }
            list.setFilterText(value);
        },
        [filter, currentFilterItem, notifyChange, list, operator, hasFilterValueChanged]
    );

    const handleOperatorChange = React.useCallback(
        (newOperator: string) => {
            // Check if operator actually changed
            if (operator === newOperator) {
                // Operator hasn't changed, no need to update
                return;
            }

            setOperator(newOperator);
            // Reset restore tracking when operator changes
            lastRestoredValueRef.current = null;
            lastRestoredOperatorRef.current = null;

            if (newOperator === "current_user" && currentUserInfo) {
                // Current user operator selected - set current user flag but save empty value (will use runtime userId)
                const newValue = "";
                const newValueLabel = currentUserInfo.name;
                const newIsCurrentUser = true;

                // Only update if values actually changed
                if (hasFilterValueChanged(newValue, newValueLabel, newOperator, newIsCurrentUser)) {
                    const updatedFilterInfo: IFilterItem[] = [
                        {
                            ...currentFilterItem,
                            type: "in",
                            value: newValue, // Empty value - will use runtime userId from BrowserStorage
                            valueLabel: newValueLabel,
                            isCurrentUser: newIsCurrentUser,
                        },
                    ];
                    const didNotify = notifyChange({
                        ...filter,
                        filterInfo: updatedFilterInfo,
                    });
                    if (didNotify) {
                        // Update last committed filter ref
                        lastCommittedFilterRef.current = {
                            value: newValue,
                            valueLabel: newValueLabel,
                            operator: newOperator,
                            isCurrentUser: newIsCurrentUser,
                        };
                    }
                }
                setSelectedKey(String(currentUserInfo.id));
                setInputValue(currentUserInfo.name);
            } else {
                // Selected user operator - clear current user flag and allow manual selection
                const newValue = currentFilterItem.value || "";
                const newValueLabel = currentFilterItem.valueLabel || "";
                const newIsCurrentUser = false;

                // Only update if values actually changed
                if (hasFilterValueChanged(newValue, newValueLabel, newOperator, newIsCurrentUser)) {
                    const updatedFilterInfo: IFilterItem[] = [
                        {
                            ...currentFilterItem,
                            type: "in",
                            value: newValue,
                            valueLabel: newValueLabel,
                            isCurrentUser: newIsCurrentUser,
                        },
                    ];
                    const didNotify = notifyChange({
                        ...filter,
                        filterInfo: updatedFilterInfo,
                    });
                    if (didNotify) {
                        // Update last committed filter ref
                        lastCommittedFilterRef.current = {
                            value: newValue,
                            valueLabel: newValueLabel,
                            operator: newOperator,
                            isCurrentUser: newIsCurrentUser,
                        };
                    }
                }
            }
        },
        [filter, currentFilterItem, notifyChange, currentUserInfo, operator, hasFilterValueChanged]
    );

    // Cleanup debounce on unmount
    React.useEffect(() => {
        return () => {
            if (apiDebounceRef.current) {
                clearTimeout(apiDebounceRef.current);
            }
        };
    }, []);

    return (
        <div ref={forwardedRef} className={styles.userFilterControl}>
            <Flex direction="column" gap="size-200">
                {/* Header: Title + Operator Picker */}
                <div className={styles.header}>
                    <Text UNSAFE_className={styles.title}>
                        {filter.label || filter.name}
                    </Text>
                    <div className={styles.operator}>
                        <OperatorSelector
                            operators={USER_OPERATORS}
                            selectedOperator={operator}
                            onOperatorChange={handleOperatorChange}
                            aria-label="User filter operator"
                        />
                    </div>
                </div>

                {/* ComboBox row */}
                <View width="100%">
                    <ComboBox
                        aria-label="User search"
                        items={list.items}
                        selectedKey={selectedKey}
                        inputValue={inputValue}
                        onInputChange={handleInputChange}
                        onSelectionChange={handleSelectionChange}
                        loadingState={list.loadingState}
                        onLoadMore={list.loadMore}
                        allowsCustomValue={false}
                        isDisabled={operator === "current_user"}
                        width="100%"
                        description="Type to search users..."
                    >
                        {(item) => (
                            <Item key={String(item.id)} textValue={item.name}>
                                {item.name}
                            </Item>
                        )}
                    </ComboBox>
                </View>
            </Flex>
        </div>
    );
});

UserFilterControl.displayName = "UserFilterControl";
